import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";

import { QuarantineService } from "@services/ingest/quarantineService.ts";
import type { QuarantinedFile } from "@models/QuarantinedFile.model";
import { useDocumentTitle } from "@/shared/hooks/useDocumentTitle";

const service = new QuarantineService();

const HEADER_SX = {
  fontWeight: 600,
  color: "#8c9097",
  fontSize: 12,
  textTransform: "uppercase",
  letterSpacing: 0.3,
} as const;

/*
    Ops view over the SFTP quarantine (MPN-1363): files the inbox router could not
    place — unknown sftp user, or a file name whose file_key matches no active
    config. The Slack "Not Routable" alert deep-links here with ?key=<s3 key> to
    highlight the file it's about. Routed-but-invalid files live on the config's
    Files page instead, where the run history shows the validation errors.
*/
export default function QuarantinePage() {
  useDocumentTitle("Quarantine");
  const [searchParams] = useSearchParams();
  const highlightKey = searchParams.get("key");
  const highlightRef = useRef<HTMLTableRowElement>(null);

  const [files, setFiles] = useState<QuarantinedFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [rowErrors, setRowErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    service
      .listFiles()
      .then(setFiles)
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load quarantine"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    highlightRef.current?.scrollIntoView({ block: "center" });
  }, [files]);

  const handleDownload = async (file: QuarantinedFile) => {
    setRowErrors((prev) => {
      const next = { ...prev };
      delete next[file.key];
      return next;
    });
    try {
      const { url } = await service.downloadFile(file.key);
      const a = document.createElement("a");
      a.href = url;
      a.download = file.file_name;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (err) {
      setRowErrors((prev) => ({
        ...prev,
        [file.key]: err instanceof Error ? err.message : "Download failed",
      }));
    }
  };

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    return (
      d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) +
      " " +
      d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })
    );
  };

  const formatSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h6" fontWeight={600}>Quarantine</Typography>
      <Typography sx={{ color: "#8c9097", fontSize: 13, mb: 2 }}>
        SFTP files that could not be routed to a config — the sender folder matched no
        organization, or the file name carried no known file key. Download the file to
        troubleshoot, then have the customer re-upload it with the correct name.
      </Typography>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <LinearProgress />
      ) : (
        <TableContainer sx={{ bgcolor: "#fff", borderRadius: 2, border: "1px solid #f3f3f3" }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: "#f9fafb" }}>
                <TableCell sx={HEADER_SX}>File</TableCell>
                <TableCell sx={HEADER_SX}>Organization</TableCell>
                <TableCell sx={HEADER_SX}>SFTP User</TableCell>
                <TableCell sx={HEADER_SX}>Size</TableCell>
                <TableCell sx={HEADER_SX}>Quarantined</TableCell>
                <TableCell sx={HEADER_SX} align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {files.map((file) => {
                const highlighted = file.key === highlightKey;
                return (
                  <TableRow
                    key={file.key}
                    ref={highlighted ? highlightRef : undefined}
                    sx={highlighted ? { bgcolor: "rgba(132,90,223,0.08)" } : undefined}
                  >
                    <TableCell sx={{ fontWeight: 500 }}>{file.file_name}</TableCell>
                    <TableCell>
                      {file.organization_name ?? (
                        <Box component="span" sx={{ color: "#e6533c" }}>Unknown</Box>
                      )}
                    </TableCell>
                    <TableCell sx={{ color: "#8c9097" }}>{file.sftp_user || "—"}</TableCell>
                    <TableCell sx={{ color: "#8c9097" }}>{formatSize(file.size)}</TableCell>
                    <TableCell sx={{ color: "#8c9097" }}>{formatDate(file.last_modified)}</TableCell>
                    <TableCell align="right">
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => handleDownload(file)}
                        startIcon={<DownloadIcon sx={{ fontSize: 14 }} />}
                        sx={{ textTransform: "none", fontSize: 12, borderColor: "#845adf", color: "#845adf" }}
                      >
                        Download
                      </Button>
                      {rowErrors[file.key] && (
                        <Typography sx={{ color: "#e6533c", fontSize: 12, mt: 0.5 }}>
                          {rowErrors[file.key]}
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          {files.length === 0 && (
            <Box sx={{ p: 4, textAlign: "center", color: "#8c9097" }}>
              <Typography>Quarantine is empty — every recent SFTP file routed cleanly.</Typography>
            </Box>
          )}
        </TableContainer>
      )}
    </Box>
  );
}
